import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Radio,
  RadioGroup,
  Checkbox,
  CheckboxGroup,
  Button,
  Image,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Stack,
  Collapse,
  useDisclosure,
} from "@chakra-ui/react";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";

const ECGGeneratorTest = () => {
  const user = useUser(api);
  
  // Check if user has admin or system-admin role
  const hasAccess = user?.roles?.some(role => 
    role.key === 'admin' || role.key === 'system-admin'
  );

  // Form state
  const [heartRate, setHeartRate] = useState(75);
  const [rhythm, setRhythm] = useState("sinus_rhythm");
  const [patientAge, setPatientAge] = useState(45);
  const [patientSex, setPatientSex] = useState("M");
  const [layout, setLayout] = useState("3x4");
  const [abnormalities, setAbnormalities] = useState([]);

  // Action hook
  const [{ data, error, fetching }, generateECG] = useGlobalAction(api.generateStandaloneECG);
  
  // Debug disclosure
  const { isOpen: isDebugOpen, onToggle: onDebugToggle } = useDisclosure();

  // Default values for reset
  const defaultValues = {
    heartRate: 75,
    rhythm: "sinus_rhythm",
    patientAge: 45,
    patientSex: "M",
    layout: "3x4",
    abnormalities: [],
  };

  const handleGenerate = async () => {
    await generateECG({
      heartRate: parseInt(heartRate),
      rhythm,
      patientAge: parseInt(patientAge),
      patientSex,
      layout,
      abnormalities,
    });
  };

  const handleClear = () => {
    setHeartRate(defaultValues.heartRate);
    setRhythm(defaultValues.rhythm);
    setPatientAge(defaultValues.patientAge);
    setPatientSex(defaultValues.patientSex);
    setLayout(defaultValues.layout);
    setAbnormalities(defaultValues.abnormalities);
  };

  // Access denied check
  if (!hasAccess) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            This tool is only available to administrators.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={8} maxW="1200px" mx="auto">
      <VStack spacing={6} align="stretch">
        {/* Header Section */}
        <Box>
          <Heading size="xl" mb={2}>
            ECG Generator Test Tool
          </Heading>
          <Text color="gray.400" mb={4}>
            Test the ECG generation system before integrating into scenarios
          </Text>
          <Alert status="info" variant="left-accent">
            <AlertIcon />
            This is a development tool for testing ECG generation functionality.
          </Alert>
        </Box>

        {/* Form Section */}
        <Card>
          <CardHeader>
            <Heading size="md">ECG Parameters</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
                {/* Heart Rate */}
                <GridItem>
                  <FormControl>
                    <FormLabel>Heart Rate</FormLabel>
                    <Input
                      type="number"
                      value={heartRate}
                      onChange={(e) => setHeartRate(e.target.value)}
                      min={30}
                      max={250}
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      30-250 BPM
                    </Text>
                  </FormControl>
                </GridItem>

                {/* Rhythm */}
                <GridItem>
                  <FormControl>
                    <FormLabel>Rhythm</FormLabel>
                    <Select value={rhythm} onChange={(e) => setRhythm(e.target.value)}>
                      <option value="sinus_rhythm">Normal Sinus Rhythm</option>
                      <option value="sinus_bradycardia">Sinus Bradycardia</option>
                      <option value="sinus_tachycardia">Sinus Tachycardia</option>
                      <option value="atrial_fibrillation">Atrial Fibrillation</option>
                      <option value="ventricular_tachycardia">Ventricular Tachycardia</option>
                      <option value="first_degree_block">First Degree Heart Block</option>
                    </Select>
                  </FormControl>
                </GridItem>

                {/* Patient Age */}
                <GridItem>
                  <FormControl>
                    <FormLabel>Patient Age</FormLabel>
                    <Input
                      type="number"
                      value={patientAge}
                      onChange={(e) => setPatientAge(e.target.value)}
                      min={0}
                      max={120}
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      0-120 years
                    </Text>
                  </FormControl>
                </GridItem>

                {/* Patient Sex */}
                <GridItem>
                  <FormControl>
                    <FormLabel>Patient Sex</FormLabel>
                    <RadioGroup value={patientSex} onChange={setPatientSex}>
                      <HStack spacing={4}>
                        <Radio value="M">Male</Radio>
                        <Radio value="F">Female</Radio>
                      </HStack>
                    </RadioGroup>
                  </FormControl>
                </GridItem>

                {/* Layout */}
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl>
                    <FormLabel>Layout</FormLabel>
                    <Select value={layout} onChange={(e) => setLayout(e.target.value)}>
                      <option value="3x4">3x4 Grid</option>
                      <option value="3x1">3x1 Strip</option>
                      <option value="6x2">6x2 Grid</option>
                      <option value="12x1">12x1 Strip</option>
                    </Select>
                  </FormControl>
                </GridItem>
              </Grid>

              {/* Abnormalities */}
              <FormControl>
                <FormLabel>Abnormalities</FormLabel>
                <CheckboxGroup value={abnormalities} onChange={setAbnormalities}>
                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={3}>
                    <Checkbox value="st_elevation">ST Elevation</Checkbox>
                    <Checkbox value="st_depression">ST Depression</Checkbox>
                    <Checkbox value="t_wave_inversion">T Wave Inversion</Checkbox>
                    <Checkbox value="lbbb">Left Bundle Branch Block</Checkbox>
                    <Checkbox value="rbbb">Right Bundle Branch Block</Checkbox>
                    <Checkbox value="qrs_prolongation">QRS Prolongation</Checkbox>
                    <Checkbox value="qt_prolongation">QT Prolongation</Checkbox>
                  </Grid>
                </CheckboxGroup>
              </FormControl>

              {/* Buttons */}
              <HStack spacing={4}>
                <Button
                  colorScheme="blue"
                  onClick={handleGenerate}
                  isLoading={fetching}
                  loadingText="Generating..."
                >
                  Generate ECG
                </Button>
                <Button variant="ghost" onClick={handleClear}>
                  Clear
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Results Section */}
        {fetching && (
          <Card>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Spinner size="xl" color="blue.500" />
                <Text fontSize="lg">Generating ECG...</Text>
              </VStack>
            </CardBody>
          </Card>
        )}

        {error && (
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Alert status="error">
                  <AlertIcon />
                  <AlertTitle>Error generating ECG</AlertTitle>
                  <AlertDescription>{error.message || String(error)}</AlertDescription>
                </Alert>
                
                <Button size="sm" onClick={onDebugToggle}>
                  {isDebugOpen ? "Hide" : "Show"} Debug Info
                </Button>
                
                <Collapse in={isDebugOpen}>
                  <Box
                    p={4}
                    bg="gray.700"
                    borderRadius="md"
                    fontFamily="mono"
                    fontSize="sm"
                    overflowX="auto"
                  >
                    <pre>{JSON.stringify(error, null, 2)}</pre>
                  </Box>
                </Collapse>
              </VStack>
            </CardBody>
          </Card>
        )}

        {data && !fetching && !error && (
          <Card>
            <CardHeader>
              <Heading size="md">Generated ECG</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                {/* ECG Image */}
                {data.ecgUrl && (
                  <Box>
                    <Image
                      src={data.ecgUrl}
                      alt="Generated ECG"
                      maxW="100%"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.600"
                    />
                  </Box>
                )}

                {/* Download Button */}
                {data.ecgUrl && (
                  <Box>
                    <Button
                      as="a"
                      href={data.ecgUrl}
                      download="ecg.svg"
                      colorScheme="green"
                    >
                      Download ECG
                    </Button>
                  </Box>
                )}

                {/* Metadata */}
                <Box>
                  <Heading size="sm" mb={4}>
                    ECG Metadata
                  </Heading>
                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                    <GridItem>
                      <Text fontWeight="bold">Heart Rate:</Text>
                      <Text>{heartRate} BPM</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontWeight="bold">Rhythm:</Text>
                      <Text>{rhythm.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontWeight="bold">Layout:</Text>
                      <Text>{layout}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontWeight="bold">Patient:</Text>
                      <Text>{patientAge}yo {patientSex === "M" ? "Male" : "Female"}</Text>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <Text fontWeight="bold">Abnormalities:</Text>
                      <Text>
                        {abnormalities.length > 0
                          ? abnormalities
                              .map((a) => a.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()))
                              .join(", ")
                          : "None"}
                      </Text>
                    </GridItem>
                  </Grid>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default ECGGeneratorTest;